import { useEffect, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, PieChart, Pie, RadarChart, PolarGrid, PolarAngleAxis, Radar,
  ScatterChart, Scatter, XAxis, YAxis, Cell, ZAxis,
} from "recharts";
import { Sparkles } from "lucide-react";
import { GaugeChart } from "../slides/GaugeChart";
import { PieGradientDefs, pieFill, PieLegend } from "../../lib/chartColors";

const COLORS = ["#8B5CF6", "#06B6D4", "#10B981", "#F59E0B", "#EC4899", "#4F46E5"];
const CATEGORY_NAMES = ["Design", "Price", "Speed", "Support"];
const SAMPLE_FRAMES = [
  [{ name: CATEGORY_NAMES[0], count: 42 }, { name: CATEGORY_NAMES[1], count: 28 }, { name: CATEGORY_NAMES[2], count: 18 }, { name: CATEGORY_NAMES[3], count: 12 }],
  [{ name: CATEGORY_NAMES[0], count: 30 }, { name: CATEGORY_NAMES[1], count: 44 }, { name: CATEGORY_NAMES[2], count: 14 }, { name: CATEGORY_NAMES[3], count: 12 }],
  [{ name: CATEGORY_NAMES[0], count: 22 }, { name: CATEGORY_NAMES[1], count: 20 }, { name: CATEGORY_NAMES[2], count: 46 }, { name: CATEGORY_NAMES[3], count: 12 }],
  [{ name: CATEGORY_NAMES[0], count: 50 }, { name: CATEGORY_NAMES[1], count: 16 }, { name: CATEGORY_NAMES[2], count: 10 }, { name: CATEGORY_NAMES[3], count: 24 }],
];

function useLiveSample(intervalMs = 2600) {
  const [frame, setFrame] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % SAMPLE_FRAMES.length), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return SAMPLE_FRAMES[frame];
}

function BarGradientDefs() {
  return (
    <defs>
      <linearGradient id="landing-bar-gradient-h" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" style={{ stopColor: "var(--brand-primary, #4F46E5)" }} />
        <stop offset="100%" style={{ stopColor: "var(--brand-accent, #FF5733)" }} />
      </linearGradient>
      <linearGradient id="landing-bar-gradient-v" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" style={{ stopColor: "var(--brand-primary, #4F46E5)" }} />
        <stop offset="100%" style={{ stopColor: "var(--brand-accent, #FF5733)" }} />
      </linearGradient>
    </defs>
  );
}

function MiniChart({ type, data }) {
  if (type === "pie" || type === "donut") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <PieGradientDefs />
          <Pie data={data} dataKey="count" nameKey="name" innerRadius={type === "donut" ? "52%" : 0} outerRadius="85%" paddingAngle={3} isAnimationActive animationDuration={900}>
            {data.map((_, i) => <Cell key={i} fill={pieFill(i)} stroke="rgba(11,15,25,0.7)" strokeWidth={2} />)}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    );
  }
  if (type === "radar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} outerRadius="70%">
          <PolarGrid stroke="rgba(255,255,255,0.08)" />
          <PolarAngleAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 11, fontFamily: "Arial, sans-serif" }} />
          <Radar dataKey="count" stroke="#06B6D4" fill="#8B5CF6" fillOpacity={0.55} isAnimationActive animationDuration={900} />
        </RadarChart>
      </ResponsiveContainer>
    );
  }
  if (type === "scatter") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 10, right: 10, bottom: 0, left: 0 }}>
          <XAxis type="category" dataKey="name" hide />
          <YAxis type="number" dataKey="count" hide />
          <ZAxis type="number" dataKey="count" range={[200, 900]} />
          <Scatter data={data} isAnimationActive animationDuration={900}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    );
  }
  if (type === "horizontal_bar") {
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ left: 0, right: 10 }}>
          <BarGradientDefs />
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" hide />
          <Bar dataKey="count" radius={[10, 10, 10, 10]} isAnimationActive animationDuration={900} background={{ fill: "rgba(255,255,255,0.08)", radius: 10 }}>
            {data.map((_, i) => <Cell key={i} fill="url(#landing-bar-gradient-h)" />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }
  if (type === "gauge") {
    const total = data.reduce((sum, d) => sum + d.count, 0) || 1;
    const pct = Math.round((data[0].count / total) * 100);
    return <GaugeChart value={pct} min={0} max={100} displayValue={`${pct}%`} label={data[0].name} dark />;
  }
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 10 }}>
        <BarGradientDefs />
        <XAxis dataKey="name" hide />
        <YAxis hide />
        <Bar dataKey="count" radius={[10, 10, 0, 0]} isAnimationActive animationDuration={900} background={{ fill: "rgba(255,255,255,0.08)", radius: [10, 10, 0, 0] }}>
          {data.map((_, i) => <Cell key={i} fill="url(#landing-bar-gradient-v)" />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

const CHARTS = [
  { type: "bar", label: "Bar chart", desc: "Classic vertical columns for quick side-by-side comparisons." },
  { type: "horizontal_bar", label: "Horizontal bar", desc: "Great for long option labels that need room to breathe." },
  { type: "pie", label: "Pie chart", desc: "See the full picture as clean proportional slices." },
  { type: "donut", label: "Donut chart", desc: "A modern ring layout with the total front and center." },
  { type: "radar", label: "Radar chart", desc: "Compare multiple options across a radial web at a glance." },
  { type: "scatter", label: "Scatter / bubble", desc: "Bubble size reveals which answers pulled the most votes." },
  { type: "gauge", label: "Speedometer / gauge", desc: "A single-metric needle sweep — perfect for Yes/No polls and average survey ratings." },
];

export function InfographicShowcase() {
  const liveData = useLiveSample();
  return (
    <section className="px-6 max-w-7xl mx-auto pb-28" data-testid="section-infographic-showcase">
      <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#4F46E5] bg-[#4F46E5]/10 px-3 py-1.5 rounded-full mb-4">
        <Sparkles className="w-3.5 h-3.5" /> Visualize it your way
      </span>
      <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mb-3">Seven chart styles for every poll &amp; survey</h2>
      <p className="text-slate-600 max-w-2xl mb-12">Pick a chart type per slide — colorful, glowing, and switchable in one click from the editor. Live preview, updating as votes roll in.</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {CHARTS.map((c) => (
          <div
            key={c.type}
            data-testid={`infographic-card-${c.type}`}
            className="relative rounded-2xl bg-[#0B0F19] border border-white/10 shadow-[0_20px_50px_rgba(15,23,42,0.25)] hover:border-[#8B5CF6]/40 hover:shadow-[0_25px_60px_rgba(139,92,246,0.2)] transition-all duration-300 p-5 overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-[#8B5CF6]/20 blur-2xl pointer-events-none" />
            <div className="h-32 mb-2 relative z-10">
              <MiniChart type={c.type} data={liveData} />
            </div>
            {(c.type === "pie" || c.type === "donut") && (
              <PieLegend data={liveData} className="mb-3 relative z-10 text-white/60" />
            )}
            <h3 className="font-heading text-base font-semibold text-white mb-1 relative z-10">{c.label}</h3>
            <p className="text-sm text-white/50 leading-relaxed relative z-10">{c.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
